import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AlertTriangle, Info, Plus, Trash2, X } from 'lucide-react';
import { adminApi } from '../../services/api';
import { PackageService } from '../../services/packageService';
import type { AdminHostAgent } from '../../types/admin';

interface AddHostAgentModalProps {
  onClose: () => void;
  onSuccess: (result: { hostAgent: AdminHostAgent; agentToken?: string }) => void;
  hostAgent?: AdminHostAgent;
}

type PackageRule = { packageId: string; maxCount: number; weightPerInstance: number };

export function AddHostAgentModal({ onClose, onSuccess, hostAgent }: AddHostAgentModalProps) {
  const isEdit = Boolean(hostAgent?._id);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '', ipAddress: '', port: 3001,
    specs: { ram: 8, cores: 4, storage: 100 },
    region: '', provider: '', isActive: false, isForFreeServers: false,
    customWorkloadsEnabled: false,
    totalWeightCapacity: 16, agentToken: '',
    sshDetails: {
      user: 'root', port: 22, password: '', keyPath: '',
      clearPassword: false, clearKeyPath: false,
    },
    allowedPackages: [] as PackageRule[],
  });
  const [availablePackages, setAvailablePackages] = useState<Array<{
    _id: string; name: string; displayName: string; isActive: boolean;
  }>>([]);

  useEffect(() => {
    let mounted = true;
    PackageService.getAllPackages(undefined, true)
      .then(packages => {
        if (mounted) setAvailablePackages(packages.map(pkg => ({
          _id: pkg._id || '', name: pkg.name, displayName: pkg.displayName, isActive: pkg.isActive,
        })));
      })
      .catch(err => mounted && setError(err instanceof Error ? err.message : 'Failed to load packages'));
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!hostAgent) return;
    setFormData(previous => ({
      ...previous,
      name: hostAgent.name,
      ipAddress: hostAgent.ipAddress,
      port: hostAgent.port,
      specs: hostAgent.specs,
      region: hostAgent.region || '',
      provider: hostAgent.provider || '',
      isActive: hostAgent.isActive,
      isForFreeServers: hostAgent.isForFreeServers,
      customWorkloadsEnabled: hostAgent.customWorkloadsEnabled,
      totalWeightCapacity: hostAgent.capacity.totalWeight,
      sshDetails: {
        ...previous.sshDetails,
        user: hostAgent.sshDetails?.user || 'root',
        port: hostAgent.sshDetails?.port || 22,
      },
      allowedPackages: hostAgent.allowedPackages.map(rule => ({
        packageId: rule.packageId,
        maxCount: rule.maxCount,
        weightPerInstance: rule.weightPerInstance,
      })),
    }));
  }, [hostAgent]);

  const createMutation = useMutation({ mutationFn: (data: any) => adminApi.createHostAgent(data) });
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => adminApi.updateHostAgent(id, data),
  });

  const setField = (field: string, value: any) => {
    setError(null);
    if (!field.includes('.')) {
      setFormData(previous => ({ ...previous, [field]: value }));
      return;
    }
    const [parent, child] = field.split('.');
    setFormData(previous => ({
      ...previous,
      [parent]: { ...(previous as any)[parent], [child]: value },
    }));
  };

  const updatePackageRule = (index: number, key: keyof PackageRule, value: string | number) => {
    setFormData(previous => ({
      ...previous,
      allowedPackages: previous.allowedPackages.map((rule, ruleIndex) => ruleIndex === index
        ? { ...rule, [key]: key === 'packageId' ? value : Number(value) }
        : rule),
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    const sshDetails: Record<string, unknown> = {
      user: formData.sshDetails.user,
      port: formData.sshDetails.port,
    };
    if (formData.sshDetails.password) sshDetails.password = formData.sshDetails.password;
    else if (formData.sshDetails.clearPassword) sshDetails.password = null;
    if (formData.sshDetails.keyPath) sshDetails.keyPath = formData.sshDetails.keyPath;
    else if (formData.sshDetails.clearKeyPath) sshDetails.keyPath = null;

    const payload: Record<string, unknown> = {
      name: formData.name,
      ipAddress: formData.ipAddress,
      port: formData.port,
      specs: formData.specs,
      region: formData.region,
      provider: formData.provider,
      isActive: formData.isActive,
      isForFreeServers: formData.isForFreeServers,
      customWorkloadsEnabled: formData.customWorkloadsEnabled,
      totalWeightCapacity: formData.totalWeightCapacity,
      allowedPackages: formData.allowedPackages,
      sshDetails,
    };
    if (!isEdit && formData.agentToken) payload.agentToken = formData.agentToken;

    try {
      const result = isEdit
        ? await updateMutation.mutateAsync({ id: hostAgent!._id, data: payload })
        : await createMutation.mutateAsync(payload);
      onSuccess(result);
    } catch (requestError: any) {
      setError(requestError?.response?.data?.error || requestError?.message || 'Operation failed');
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content max-w-3xl max-h-[92vh] overflow-y-auto" onClick={event => event.stopPropagation()}>
        <div className="flex justify-between items-center p-6 border-b border-dark-600">
          <div>
            <h2 className="text-xl font-bold text-gray-100">{isEdit ? 'Edit Host Agent' : 'Add Host Agent'}</h2>
            <p className="text-xs text-gray-500 mt-1">Mothership endpoint, allocation policy and host SSH target</p>
          </div>
          <button onClick={onClose} className="btn-ghost p-2"><X className="w-5 h-5" /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && <div className="p-3 rounded border border-red-500/30 bg-red-500/10 text-sm text-red-300">{error}</div>}

          <section>
            <h3 className="section-title mb-4">Agent endpoint</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="text-sm text-gray-300">Name
                <input value={formData.name} onChange={e => setField('name', e.target.value)} className="input mt-1" placeholder="agent-eu-1" required />
              </label>
              <label className="text-sm text-gray-300">IP address or hostname
                <input value={formData.ipAddress} onChange={e => setField('ipAddress', e.target.value)} className="input mt-1" placeholder="10.0.0.12" required />
              </label>
              <label className="text-sm text-gray-300">Host-Agent port
                <input type="number" min={1} max={65535} value={formData.port} onChange={e => setField('port', Number(e.target.value))} className="input mt-1" required />
              </label>
              <label className="text-sm text-gray-300">Provider
                <input value={formData.provider} onChange={e => setField('provider', e.target.value)} className="input mt-1" placeholder="hetzner" />
              </label>
              <label className="text-sm text-gray-300">Region
                <input value={formData.region} onChange={e => setField('region', e.target.value)} className="input mt-1" placeholder="eu-central" />
              </label>
              <label className="text-sm text-gray-300">Weight capacity
                <input type="number" min={1} value={formData.totalWeightCapacity} onChange={e => setField('totalWeightCapacity', Number(e.target.value))} className="input mt-1" required />
              </label>
            </div>
            <div className="flex flex-wrap gap-6 mt-4">
              <label className="flex items-center gap-2 text-sm text-gray-300">
                <input type="checkbox" checked={formData.isActive} onChange={e => setField('isActive', e.target.checked)} className="checkbox" /> Enable Socket.IO connection
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-300">
                <input type="checkbox" checked={formData.isForFreeServers} onChange={e => setField('isForFreeServers', e.target.checked)} className="checkbox" /> Free tier host
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-300">
                <input type="checkbox" checked={formData.customWorkloadsEnabled} onChange={e => setField('customWorkloadsEnabled', e.target.checked)} className="checkbox" /> Custom workloads opt-in
              </label>
            </div>
          </section>

          <section>
            <h3 className="section-title mb-4">Declared host capacity</h3>
            <div className="grid grid-cols-3 gap-4">
              {(['ram', 'cores', 'storage'] as const).map(key => (
                <label key={key} className="text-sm text-gray-300 capitalize">{key} {key !== 'cores' && '(GB)'}
                  <input type="number" min={0.5} value={formData.specs[key]} onChange={e => setField(`specs.${key}`, Number(e.target.value))} className="input mt-1" required />
                </label>
              ))}
            </div>
          </section>

          {!isEdit && (
            <section>
              <h3 className="section-title mb-3">Agent token</h3>
              <input type="password" value={formData.agentToken} onChange={e => setField('agentToken', e.target.value)} className="input" placeholder="Leave blank to generate a 64-character token" />
              <p className="text-xs text-gray-500 mt-2">The saved token is shown once after create. Copy it to <code>HOST_AGENT_TOKEN</code>.</p>
            </section>
          )}

          <section>
            <h3 className="section-title mb-4">SSH target</h3>
            <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg mb-4 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-yellow-400 shrink-0 mt-0.5" />
              <p className="text-sm text-yellow-300">This connects Mothership directly to the host's sshd. Blank secret fields preserve existing values while editing.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="text-sm text-gray-300">Username
                <input value={formData.sshDetails.user} onChange={e => setField('sshDetails.user', e.target.value)} className="input mt-1" required />
              </label>
              <label className="text-sm text-gray-300">SSH port
                <input type="number" min={1} max={65535} value={formData.sshDetails.port} onChange={e => setField('sshDetails.port', Number(e.target.value))} className="input mt-1" required />
              </label>
              <label className="text-sm text-gray-300">Password
                <input type="password" value={formData.sshDetails.password} onChange={e => setField('sshDetails.password', e.target.value)} className="input mt-1" placeholder={hostAgent?.sshDetails?.passwordConfigured ? 'Configured — leave blank to keep' : 'Optional'} />
              </label>
              <label className="text-sm text-gray-300">Private key path on Mothership
                <input value={formData.sshDetails.keyPath} onChange={e => setField('sshDetails.keyPath', e.target.value)} className="input mt-1" placeholder={hostAgent?.sshDetails?.keyConfigured ? 'Configured — leave blank to keep' : 'C:\\keys\\host.pem'} />
              </label>
            </div>
            {isEdit && (
              <div className="flex flex-wrap gap-6 mt-3">
                <label className="flex items-center gap-2 text-xs text-gray-400"><input type="checkbox" className="checkbox" checked={formData.sshDetails.clearPassword} onChange={e => setField('sshDetails.clearPassword', e.target.checked)} /> Clear saved password</label>
                <label className="flex items-center gap-2 text-xs text-gray-400"><input type="checkbox" className="checkbox" checked={formData.sshDetails.clearKeyPath} onChange={e => setField('sshDetails.clearKeyPath', e.target.checked)} /> Clear saved key path</label>
              </div>
            )}
          </section>

          <section>
            <div className="flex justify-between items-center mb-4">
              <h3 className="section-title">Package allocation rules</h3>
              <button type="button" className="btn-secondary text-sm py-1.5 px-3" onClick={() => setFormData(previous => ({
                ...previous,
                allowedPackages: [...previous.allowedPackages, { packageId: '', maxCount: 1, weightPerInstance: 1 }],
              }))}><Plus className="w-4 h-4" /> Add rule</button>
            </div>
            <div className="space-y-3">
              {formData.allowedPackages.map((rule, index) => (
                <div key={`${rule.packageId}-${index}`} className="grid grid-cols-12 gap-3 items-end">
                  <label className="col-span-6 text-xs text-gray-400">Package
                    <select value={rule.packageId} onChange={e => updatePackageRule(index, 'packageId', e.target.value)} className="input text-sm mt-1" required>
                      <option value="">Select</option>
                      {availablePackages.map(pkg => <option key={pkg._id} value={pkg._id}>{pkg.displayName}{pkg.isActive ? '' : ' (inactive)'}</option>)}
                    </select>
                  </label>
                  <label className="col-span-2 text-xs text-gray-400">Max
                    <input type="number" min={1} value={rule.maxCount} onChange={e => updatePackageRule(index, 'maxCount', e.target.value)} className="input text-sm mt-1" />
                  </label>
                  <label className="col-span-3 text-xs text-gray-400">Weight
                    <input type="number" min={1} value={rule.weightPerInstance} onChange={e => updatePackageRule(index, 'weightPerInstance', e.target.value)} className="input text-sm mt-1" />
                  </label>
                  <button type="button" onClick={() => setFormData(previous => ({ ...previous, allowedPackages: previous.allowedPackages.filter((_, i) => i !== index) }))} className="col-span-1 btn-ghost p-2 text-red-400"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
              {formData.allowedPackages.length === 0 && <p className="text-sm text-gray-500">No package can be allocated until a rule is added.</p>}
            </div>
          </section>

          <div className="p-4 bg-cyber-500/10 border border-cyber-500/20 rounded-lg flex items-start gap-3">
            <Info className="w-5 h-5 text-cyber-400 shrink-0" />
            <p className="text-sm text-cyber-300">Health checks validate authenticated REST. Connection control validates the persistent Mothership → Agent Socket.IO channel separately.</p>
          </div>

          <div className="flex justify-end gap-3 pt-5 border-t border-dark-600">
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
            <button type="submit" disabled={isPending} className="btn-primary">{isPending ? 'Saving…' : (isEdit ? 'Save Agent' : 'Add Agent')}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
